// Single source of truth for the "relaunch" design (docs/Layout-Startseite.pdf).
// Every class string here is pinned by src/lib/relaunchLayout.test.ts - change
// the PDF spec there first, then here, then let the components follow.

export type RelaunchImagePosition = 'left' | 'right';

// Hero banner: never the full-viewport treatment; height comes from the
// --hero-height var (70vh default in globals.css, editable in Strapi's Site
// Config as "Hero height (vh)").
export const RELAUNCH_HERO_HEIGHT_CLASS = 'h-[var(--hero-height,70vh)]';

// Page sections shrink to a shared minimum (default 70vh, set in globals.css
// as --start-page-section-min-h) so the next section already peeks into view
// while scrolling - but grow freely when their content is taller.
export const RELAUNCH_SECTION_MIN_H_CLASS = 'min-h-[var(--start-page-section-min-h)]';

// Flat, solid-green small-radius button - the PDF's only CTA style.
export const RELAUNCH_CTA_CLASS =
  'teaser-cta inline-block bg-green-700 hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-800 text-white px-6 py-2 rounded-md font-medium transition-colors';

// Width-capped, centered, with the shared horizontal padding var (defined in
// globals.css as --start-page-padding-x) so every relaunch module aligns.
export function relaunchContainerClass(): string {
  return 'teaser teaser-relaunch w-full max-w-5xl mx-auto px-[var(--start-page-padding-x)]';
}

// Teasers with body copy get the PDF's uppercase heading (HILF MIR, ÜBER
// CLARA GRUNWALD); the textless teaser (Ferienplan) keeps regular case.
export function relaunchHeadingClass(textless: boolean): string {
  return textless
    ? 'teaser-title font-normal text-gray-900 dark:text-gray-100'
    : 'teaser-title uppercase font-bold text-gray-900 dark:text-gray-100';
}

// Body copy sits in a bordered rounded card, the narrower side of the 4:3 split.
export const RELAUNCH_TEXT_PANEL_CLASS =
  'w-full md:flex-[3] flex flex-col justify-center rounded-2xl border border-gray-200 dark:border-gray-700 p-6';

// Image column: wider (4) share of the split beside copy, full width when textless.
export function relaunchImageColClass(textless: boolean): string {
  return textless
    ? 'teaser-image w-full relative overflow-hidden rounded-lg'
    : 'teaser-image w-full md:flex-[4] relative overflow-hidden rounded-lg';
}

// Column render order; textless mode has no text panel at all.
export function relaunchColumnOrder(
  imagePosition: RelaunchImagePosition,
  hasCopy: boolean
): Array<'image' | 'text'> {
  if (!hasCopy) return ['image'];
  return imagePosition === 'right' ? ['text', 'image'] : ['image', 'text'];
}

// Dense triple-tease ("Aktuelle Themen") in the relaunch design: aligned with
// the teaser modules above (same width cap + padding var), left-aligned
// heading, tight card gap, plain gray-bordered cards.
export const RELAUNCH_TRIPLE = {
  containerClass: 'w-full max-w-5xl mx-auto px-[var(--start-page-padding-x)] py-4',
  headingClass: 'text-2xl font-normal text-gray-900 dark:text-gray-100 mb-4',
  gridClass: 'flex flex-col md:flex-row md:items-stretch gap-4 w-full',
  cardClass:
    'flex-1 flex rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm bg-background overflow-hidden',
} as const;
